import React, { useState, useEffect } from "react";
import axios from "axios";
import { Flex } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { getUuid } from "@util/getUuid";
import { AssetManagement, AssetLibraryPicker } from "@components/Asset";
import {
  addFileToConfig,
  formatPlacements,
  renameFileInConfig,
  removeFileInConfig,
} from "@components/Asset/helpers";

import { placementDefs } from "@components/Asset/placementDefs";

export default function ConfigAssets({
  campaignId,
  contestId,
  effortId,
  lookAndFeelTemplateId,
  updateConfig,
  jsonData,
}) {
  const { t } = useTranslation();
  const [assets, setAssets] = useState([]);

  useEffect(() => {
    fetchAssets();
  }, [JSON.stringify(jsonData)]);

  function fetchAssets() {
    axios
      .post(`/retrieve-assets/`, {
        campaign_effort_id: effortId,
        contest_id: contestId,
        campaign_id: campaignId,
        look_and_feel_template_id: lookAndFeelTemplateId,
      })
      .then(({ data }) => {
        setAssets(
          data.response[0].data.map((d) => ({
            ...d,
            ...d.asset,
          }))
        );
      })
      .catch((err) => {
        setAssets([]);
      });
  }

  function onUploadSuccess(newId, placementValue) {
    updateConfig(addFileToConfig(jsonData, newId, placementValue));
  }

  function onRemoveSuccess(removedId) {
    updateConfig(removeFileInConfig(jsonData, removedId));
  }

  function onRenameSuccess(assetId, newName) {
    updateConfig(renameFileInConfig(jsonData, { id: assetId, name: newName }));
  }

  function onLibrarySelection(itemId, placementValue) {
    const req = {
      campaign_effort_id: effortId,
      campaign_id: campaignId,
      contest_id: contestId,
      look_and_feel_template_id: lookAndFeelTemplateId,
      asset_id: itemId,
      variety: effortId ? 2 : campaignId ? 3 : contestId ? 5 : null,
    };

    axios
      .post(`/replicate-library-asset/`, req)
      .then(({ data }) => {
        onUploadSuccess(data.response[0].id, placementValue);
      })
      .catch((err) => {
        fetchAssets();
        toast.error(err);
      });
  }

  const placements = [
    {
      text: t("mixer:microsite_settings:info_modal_featured_image"),
      value: placementDefs.featured,
      limit: 1,
    },
    {
      text: t("mixer:microsite_settings:site_background"),
      value: placementDefs.background,
      limit: 1,
    },
    {
      text: t("mixer:microsite_settings:header_logo"),
      value: placementDefs.header_logo,
      limit: 1,
    },
  ].map((m) => ({
    ...m,
    key: getUuid(),
  }));

  const formattedPlacements = formatPlacements(placements, jsonData, assets);

  return (
    <React.Fragment>
      {formattedPlacements.map((p) => (
        <AssetManagement
          library={false}
          campaignId={campaignId}
          contestId={contestId}
          effortId={effortId}
          lookAndFeelTemplateId={lookAndFeelTemplateId}
          items={p.assets}
          title={
            <Flex align="center" gap="xs">
              {p.text}{" "}
              {p.limit && (
                <span>
                  ({p.assets.length} / {p.limit})
                </span>
              )}
            </Flex>
          }
          key={p.value}
          limit={p.limit}
          onRenameSuccess={onRenameSuccess}
          onRemoveSuccess={onRemoveSuccess}
          onUploadSuccess={(newId) => onUploadSuccess(newId, p.value)}
          shouldFetch={false}
        >
          {p.assets.length < p.limit && (
            <AssetLibraryPicker
              onClose={fetchAssets}
              onSelect={(assetId) => onLibrarySelection(assetId, p.value)}
              reqData={{
                variety: [2, 3, 4],
              }}
            />
          )}
        </AssetManagement>
      ))}
    </React.Fragment>
  );
}
